import { useParams, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, ArrowLeft, Calendar, Clock, Share2, User, Tag, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import SEO from "../components/SEO";
import { articlesData } from "./BlogPage";

const SITE_URL = "https://ombudnow.com";

// Create Article Schema for SEO
const createArticleSchema = (article, url) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": article.title,
  "description": article.metaDescription || article.summary,
  "image": article.image || `${SITE_URL}/og-image.png`,
  "author": {
    "@type": "Organization",
    "name": article.author || "OmbudNow"
  },
  "publisher": {
    "@type": "Organization",
    "name": "OmbudNow",
    "logo": {
      "@type": "ImageObject",
      "url": `${SITE_URL}/logo.png`
    }
  },
  "datePublished": article.date,
  "dateModified": article.date,
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": url
  }
});

// Create Breadcrumb Schema
const createBreadcrumbSchema = (article, lang, slug) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": SITE_URL
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Blog",
      "item": `${SITE_URL}/${lang}/blog`
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": article.title,
      "item": `${SITE_URL}/${lang}/blog/${slug}`
    }
  ]
});

// Create FAQ Schema
const createFAQSchema = (faqs) => {
  if (!faqs || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Create HowTo Schema (for practical guides)
const createHowToSchema = (article, steps) => {
  if (!steps || steps.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": article.title,
    "description": article.metaDescription || article.summary,
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text
    }))
  };
};

export default function BlogArticle() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Detect language from URL
  const isEnglish = location.pathname.startsWith('/en');
  const currentLang = isEnglish ? 'en' : 'pt';
  
  // Find article in both languages
  const allArticles = [...articlesData.pt, ...articlesData.en];
  const article = allArticles.find(a => a.slug === slug);
  
  const t = {
    pt: {
      backToBlog: "Voltar ao Blog",
      share: "Compartilhar",
      shareSuccess: "Link copiado!",
      notFound: "Artigo não encontrado",
      notFoundDesc: "O artigo que você procura não existe ou foi removido.",
      goToBlog: "Ir para o Blog",
      comingSoon: "Conteúdo em breve",
      comingSoonDesc: "Este artigo está sendo preparado e será publicado em breve. Volte mais tarde!",
      relatedArticles: "Artigos relacionados",
      by: "Por",
      readTime: "de leitura",
      tableOfContents: "Índice",
      cta: {
        title: "Pronto para implementar compliance?",
        description: "Crie seu canal de denúncias e ouvidoria em minutos.",
        button: "Começar agora - 7 dias grátis"
      }
    },
    en: {
      backToBlog: "Back to Blog",
      share: "Share",
      shareSuccess: "Link copied!",
      notFound: "Article not found",
      notFoundDesc: "The article you're looking for doesn't exist or has been removed.",
      goToBlog: "Go to Blog",
      comingSoon: "Content coming soon",
      comingSoonDesc: "This article is being prepared and will be published soon. Check back later!",
      relatedArticles: "Related articles",
      by: "By",
      readTime: "read",
      tableOfContents: "Table of Contents",
      cta: {
        title: "Ready to implement compliance?",
        description: "Create your whistleblowing and ombudsman channel in minutes.",
        button: "Start now - 7 days free"
      }
    }
  }[currentLang];

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: article?.title,
          text: article?.summary,
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(t.shareSuccess);
      }
    } catch {
      await navigator.clipboard.writeText(url);
      toast.success(t.shareSuccess);
    }
  };

  // Article not found
  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100/50 via-white to-white pointer-events-none" />
        <div className="relative z-10 text-center">
          <Shield className="w-16 h-16 text-white/20 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-4 font-chivo">{t.notFound}</h1>
          <p className="text-slate-400 mb-8">{t.notFoundDesc}</p>
          <Link to={`/${currentLang}/blog`}>
            <Button className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-full px-8 hover:shadow-[0_0_20px_rgba(168,85,247,0.4)]">
              {t.goToBlog}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Get related articles (same language, different article)
  const articleLang = articlesData.pt.find(a => a.slug === slug) ? 'pt' : 'en';
  const relatedArticles = articlesData[articleLang]
    .filter(a => a.slug !== slug)
    .slice(0, 2);

  const articleUrl = `${SITE_URL}/${currentLang}/blog/${slug}`;
  const articleSchema = createArticleSchema(article, articleUrl);
  const breadcrumbSchema = createBreadcrumbSchema(article, currentLang, slug);
  
  // Build schemas array
  const schemas = [articleSchema, breadcrumbSchema];
  
  // Add FAQ schema if article has FAQs
  if (article.faqs && article.faqs.length > 0) {
    const faqSchema = createFAQSchema(article.faqs);
    if (faqSchema) schemas.push(faqSchema);
  }
  
  // Add HowTo schema if article has steps
  if (article.howToSteps && article.howToSteps.length > 0) {
    const howToSchema = createHowToSchema(article, article.howToSteps);
    if (howToSchema) schemas.push(howToSchema);
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-100/50 via-white to-white pointer-events-none" />
      <SEO
        title={`${article.title} | Blog OmbudNow`}
        description={article.metaDescription || article.summary}
        canonical={`/${currentLang}/blog/${slug}`}
        lang={currentLang}
        keywords={article.category ? `${article.category}, compliance, whistleblowing, ombudsman` : 'compliance, whistleblowing, ombudsman'}
        schemas={schemas}
        image={article.image}
      />
      
      {/* Header */}
      <header className="relative z-50 px-4 sm:px-6 py-4 sticky top-0 bg-white/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center group-hover:shadow-[0_0_20px_rgba(168,85,247,0.4)] transition-all">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900 font-chivo">OMBUDNOW</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link to={`/${currentLang}/blog`}>
              <Button variant="ghost" className="text-slate-600 hover:text-slate-900 hover:bg-white/10 rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t.backToBlog}
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="sm"
              className="border-white/20 text-white hover:bg-white/10 rounded-full"
              onClick={handleShare}
              data-testid="share-article-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              {t.share}
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 px-6 py-8 md:py-16">
        <article className="max-w-4xl mx-auto" itemScope itemType="https://schema.org/Article">
          
          {/* Hero Section with Image */}
          {article.image && (
            <div className="relative w-full h-64 md:h-96 rounded-2xl overflow-hidden mb-8">
              <img 
                src={article.image} 
                alt={article.title}
                className="w-full h-full object-cover"
                itemProp="image"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            </div>
          )}

          {/* Article Header */}
          <header className="mb-10">
            {/* Category Badge */}
            {article.category && (
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-blue-600 border border-indigo-300">
                  <Tag className="w-3 h-3" />
                  {article.category}
                </span>
              </div>
            )}

            {/* Title */}
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight font-chivo tracking-tight"
              itemProp="headline"
            >
              {article.title}
            </h1>
            
            {/* Summary / Lead */}
            <p className="text-xl text-slate-300 leading-relaxed mb-6" itemProp="description">
              {article.summary}
            </p>

            {/* Meta Info Bar */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 pb-6 border-b border-white/10">
              {article.author && (
                <span className="flex items-center gap-2" itemProp="author" itemScope itemType="https://schema.org/Organization">
                  <User className="w-4 h-4" />
                  <span itemProp="name">{t.by} {article.author}</span>
                </span>
              )}
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <time dateTime={article.date} itemProp="datePublished">
                  {new Date(article.date).toLocaleDateString(currentLang === 'pt' ? 'pt-BR' : 'en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {article.readTime} {t.readTime}
              </span>
            </div>
          </header>

          {/* Article Content */}
          {article.content ? (
            <div 
              className="article-content max-w-none"
              itemProp="articleBody"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          ) : (
            <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-12 text-center">
              <Shield className="w-16 h-16 text-[#f5a623]/50 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-4">{t.comingSoon}</h2>
              <p className="text-slate-400 max-w-md mx-auto">
                {t.comingSoonDesc}
              </p>
            </div>
          )}

          {/* CTA Section - Uses article-specific CTA if available */}
          {article.content && (
            <section className="mt-16 bg-gradient-to-r from-[#f5a623]/20 to-[#0f3460]/50 rounded-2xl border border-[#f5a623]/30 p-8 md:p-12 text-center relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#f5a623]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#f5a623]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f5a623]/20 rounded-full text-[#f5a623] text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  {currentLang === 'pt' ? '7 dias grátis' : '7 days free trial'}
                </div>
                
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {article.cta?.title || t.cta.title}
                </h2>
                <p className="text-slate-300 mb-8 max-w-xl mx-auto text-lg">
                  {article.cta?.description || t.cta.description}
                </p>
                
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/#pricing">
                    <Button 
                      size="lg"
                      className="bg-[#f5a623] hover:bg-[#e09612] text-[#1a1a2e] font-semibold px-8"
                      data-testid="article-cta-btn"
                    >
                      {article.cta?.button || t.cta.button}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to={currentLang === 'pt' ? '/pt/contato' : '/en/contact'}>
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-white/30 text-white hover:bg-white/10"
                    >
                      {currentLang === 'pt' ? 'Falar com especialista' : 'Talk to an expert'}
                    </Button>
                  </Link>
                </div>
                
                <p className="text-slate-500 text-sm mt-6">
                  {currentLang === 'pt' 
                    ? 'Sem cartão de crédito • Cancele quando quiser' 
                    : 'No credit card required • Cancel anytime'}
                </p>
              </div>
            </section>
          )}

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-12 border-t border-white/10">
              <h2 className="text-2xl font-bold text-white mb-8">{t.relatedArticles}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedArticles.map((related) => (
                  <Link 
                    key={related.id}
                    to={`/${articleLang}/blog/${related.slug}`}
                    className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-hidden hover:border-[#f5a623]/50 transition-all group"
                  >
                    {related.image && (
                      <div className="h-32 overflow-hidden">
                        <img 
                          src={related.image} 
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#f5a623] transition-colors">
                        {related.title}
                      </h3>
                      <p className="text-slate-400 text-sm line-clamp-2">
                        {related.summary}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            {currentLang === 'en' ? '© 2025 OmbudNow. All rights reserved.' : '© 2025 OmbudNow. Todos os direitos reservados.'}
          </p>
          <div className="flex items-center gap-4">
            <Link to={currentLang === 'en' ? '/en/privacy-policy' : '/pt/politica-de-privacidade'} className="text-slate-600 hover:text-slate-900 text-sm transition-colors">
              {currentLang === 'en' ? 'Privacy Policy' : 'Política de Privacidade'}
            </Link>
            <Link to={currentLang === 'en' ? '/en/terms-of-use' : '/pt/termos-de-uso'} className="text-slate-600 hover:text-slate-900 text-sm transition-colors">
              {currentLang === 'en' ? 'Terms of Use' : 'Termos de Uso'}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
